import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { TripWaypoint, TripWaypointETA, Task } from "@/api.types.v2";

const API_URL = import.meta.env.VITE_API_URL;

export interface SplitWaypointInput {
  tripId: string;
  waypointId: string;
  taskId: string;
  sourceTaskIds: string[];
  sourceAnchorTaskId: string;
  expectedSourcePin: string;
  expectedMovedPin: string;
  expectedRemainingPin: string;
}

export function useSplitWaypoint() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tripId, waypointId, ...body }: SplitWaypointInput) => {
      const { data } = await axios.post<{ routeNeedsRefresh: boolean }>(
        `${API_URL}/api/v2/trips/${tripId}/waypoints/${waypointId}/split`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async (_result, { tripId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["waypoints", tripId] }),
        queryClient.invalidateQueries({ queryKey: ["trips", tripId] }),
        queryClient.invalidateQueries({ queryKey: ["all-waypoints"] }),
      ]);
    },
  });
}

// Get all waypoints for a trip
export function useGetTripWaypoints(tripId: string) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["trip-waypoints", tripId],
    queryFn: async () => {
      const { data } = await axios.get<{
        waypoints: TripWaypoint[];
      }>(`${API_URL}/api/v2/trips/${tripId}/waypoints`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data.waypoints;
    },
    enabled: !!token && !!tripId,
  });
}

// Get a specific waypoint
export function useWaypointById(tripId: string, waypointId: string) {
  const { token } = useSession();

  return useQuery<TripWaypoint>({
    queryKey: ["waypoints", tripId, waypointId],
    queryFn: async () => {
      const { data } = await axios.get<TripWaypoint>(
        `${API_URL}/api/v2/trips/${tripId}/waypoints/${waypointId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!token && !!tripId && !!waypointId,
  });
}

// Create waypoint for a trip
export function useCreateWaypoint(options?: {
  onSuccess?: (data: TripWaypoint) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      tripId,
      waypoint,
    }: {
      tripId: string;
      waypoint: {
        sequence: number;
        locationKey: string;
        radius?: number;
      };
    }) => {
      const { data } = await axios.post<TripWaypoint>(
        `${API_URL}/api/v2/trips/${tripId}/waypoints`,
        waypoint,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

// Update waypoint
export function useUpdateWaypoint(options?: {
  onSuccess?: (data: TripWaypoint) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      tripId,
      waypointId,
      updates,
    }: {
      tripId: string;
      waypointId: string;
      updates: {
        sequence?: number;
        locationKey?: string;
        radius?: number;
      };
    }) => {
      const { data } = await axios.patch<TripWaypoint>(
        `${API_URL}/api/v2/trips/${tripId}/waypoints/${waypointId}`,
        updates,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

// Update waypoint ETA
export function useUpdateWaypointETA(options?: {
  onSuccess?: (data: TripWaypointETA) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      waypointId,
      eta,
    }: {
      waypointId: string;
      eta: {
        estimatedArrivalTime: string;
        estimatedDepartureTime: string;
        estimatedDuration: number;
        actualArrivalTime?: string;
        actualDepartureTime?: string;
      };
    }) => {
      const { data } = await axios.patch<TripWaypointETA>(
        `${API_URL}/api/v2/waypoints/${waypointId}/eta`,
        eta,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

// Delete waypoint
export function useDeleteWaypoint(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      tripId,
      waypointId,
    }: {
      tripId: string;
      waypointId: string;
    }) => {
      await axios.delete(
        `${API_URL}/api/v2/trips/${tripId}/waypoints/${waypointId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

// Reorder waypoints in a trip
export function useReorderWaypoints(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      tripId,
      waypointIds,
    }: {
      tripId: string;
      waypointIds: string[];
    }) => {
      await axios.put(
        `${API_URL}/api/v2/trips/${tripId}/waypoints/reorder`,
        { waypointIds },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

// Get tasks for a waypoint
export function useGetWaypointTasks(waypointId: string) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["waypoint-tasks", waypointId],
    queryFn: async () => {
      const { data } = await axios.get<{
        tasks: Task[];
      }>(`${API_URL}/api/v2/waypoints/${waypointId}/tasks`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data.tasks;
    },
    enabled: !!token && !!waypointId,
  });
}

// Assign task to waypoint
export function useAssignTaskToWaypoint(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      taskId,
      waypointId,
    }: {
      taskId: string;
      waypointId: string;
    }) => {
      await axios.patch(
        `${API_URL}/api/v2/tasks/${taskId}`,
        { waypointId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

// Calculate ETAs for all waypoints in a trip
export function useCalculateTripETAs(options?: {
  onSuccess?: (data: TripWaypointETA[]) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (tripId: string) => {
      const { data } = await axios.post<{
        etas: TripWaypointETA[];
      }>(
        `${API_URL}/api/v2/trips/${tripId}/calculate-etas`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data.etas;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

// Start all tasks at a waypoint (sends ARRIVING_SOON notifications)
export function useStartWaypoint(options?: {
  onSuccess?: (data: { message: string; startedTaskIds: string[] }) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      tripId,
      waypointId,
    }: {
      tripId: string;
      waypointId: string;
    }) => {
      const { data } = await axios.post<{
        message: string;
        startedTaskIds: string[];
      }>(
        `${API_URL}/api/v2/trips/${tripId}/waypoints/${waypointId}/start`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}
